import type { Environment } from "../types";

const CONTENT_BUCKET = "https://ithuman-web.s3.eu-central-1.amazonaws.com";

// Coalesces concurrent calls for the same key into one in-flight request —
// e.g. ContentCell and FeaturesDisplay both fetch alias/features data for
// the same row at nearly the same time. Not a persistent cache: each entry
// is dropped once its request settles, so a later independent call (e.g.
// after a manual refresh) still hits the network for fresh data.
const inFlight = new Map<string, Promise<unknown>>();

function coalesce<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const promise = run().finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
}

export async function fetchAlias(env: Environment, solution: string): Promise<string> {
  const aliases = await coalesce(`aliases:${env}`, async () => {
    const response = await fetch(`${CONTENT_BUCKET}/${env}/aliases.json`, { cache: "no-store" });
    if (!response.ok) {
      throw new Error("Not able to fetch aliases");
    }
    const data: Record<string, string> = await response.json();
    return data;
  });
  return aliases[solution] ?? solution;
}

export interface FeaturesData {
  features: string[];
}

export async function fetchFeatures(env: Environment, alias: string): Promise<FeaturesData | null> {
  return coalesce(`features:${env}:${alias}`, async () => {
    const response = await fetch(`${CONTENT_BUCKET}/${env}/${alias}/features.json`);
    if (!response.ok) {
      return null;
    }
    return response.json();
  });
}

export function contentConsoleUrl(env: Environment, alias: string): string {
  const base = "https://eu-central-1.console.aws.amazon.com/s3/buckets/ithuman-web";
  const params = "?region=eu-central-1&bucketType=general";
  const prefix = `&prefix=${env}/${alias}/&showversions=false`;
  return `${base}${params}${prefix}`;
}
